import { Box, Slider, styled, Typography, useTheme } from '@mui/material';
import { vec2 } from 'gl-matrix';
import { useCallback, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import EditableNumericValue from '~/components/EditableNumericValue';
import useDose from '~/hooks/patient/dose/useDose';
import useViewportFocus from '~/hooks/useViewportFocus';
import {
    doseVisibilityState,
    selectedDoseRangeState,
} from '~/state/atoms/dose/doseAtoms';
import { relativeDosePercent } from '~/utils/dose/doseDisplayValidation';
import { generateDoseLevelLabel } from './doseColorwashLabels';
import DoseOriginLabel from './DoseOriginLabel';
import DoseVisibilityToggle from './DoseVisibilityToggle';
import { NavigateToMaxButton } from './NavigateToMaxButton';
import { OverlayLabel, OverlayRow } from '~/components/OverlayLabel';
import { useCurrentDose } from '~/hooks/dose/useCurrentDose';

export const calculateColorwashStep = (
    relativeDoseInPercent?: number
): number => {
    return relativeDoseInPercent ? 1 / relativeDoseInPercent : 0.01;
};

export const relativeToAbsoluteDose = (
    value: number,
    maxDose: number
): string => (value * maxDose).toFixed(1);

/**
 * Convert UI percent-of-prescription input to the slider's normalised
 * 0..1-of-max-dose range. Inverse of the `value * relativeDoseInPercent`
 * formula used in the per-thumb value label.
 */
const percentToSliderValue = (
    pct: number,
    relativeDoseInPercent: number | undefined
): number => {
    return relativeDoseInPercent ? pct / relativeDoseInPercent : pct / 100;
};

/**
 * Round a normalised slider value to the nearest valid step. Prescription-
 * normalised doses tick at exactly 1 %, but a non-normalised dose has a
 * sub-percent step, so a typed integer percent may not fall on a step.
 */
const snapToStep = (sliderValue: number, step: number): number => {
    if (step <= 0) {
        return sliderValue;
    }
    return Math.round(sliderValue / step) * step;
};

const ColorwashSlider = styled(Slider)(({ theme }) => ({
    height: 200,
    width: 16,
    boxSizing: 'border-box',
    pointerEvents: 'auto',
    '& .MuiSlider-thumb': {
        border: '1px solid #fff',
        backgroundColor: `${theme.palette.primary.main}`,
        width: 20,
        height: 8,
        borderRadius: 0,
    },
    '& .MuiSlider-valueLabel': {
        backgroundColor: 'transparent',
        padding: 0,
        left: '46px',
        top: '12px',
        // Allow the editable percent inside the value label to receive
        // clicks/keys without the thumb stealing the gesture.
        pointerEvents: 'auto',
    },
    '& .MuiSlider-mark': {
        background: 'transparent',
    },
    '& .MuiSlider-rail': {
        height: '100%',
        background:
            'linear-gradient(to bottom, red, orange, yellow, lightgreen, lightblue, blue)',
        opacity: 0.7,
        borderRadius: 0,
    },
    '& .MuiSlider-track': {
        background: 'transparent',
        border: `3px solid ${theme.palette.background.paper}`,
        borderLeft: 'none',
        borderRight: 'none',
        borderRadius: 0,
    },
}));

export const DoseColorwashSlider = () => {
    const theme = useTheme();
    const intl = useIntl();
    const { dose, previewDoseRange, commitDoseRange } = useDose();
    const doseRange = useRecoilValue(selectedDoseRangeState);
    const currentDose = useCurrentDose();
    const doseVisibility = useRecoilValue(doseVisibilityState);
    const { focusOnPoint } = useViewportFocus();
    const doseScale = dose?.doseScale ?? 1.0;

    const maxDosePosition = currentDose?.doseMaxPosition ?? null;
    // Display-scaled max (raw max × UI display scale), from the display view.
    const maxDoseGyDisplay = dose?.displayMaxDoseGy ?? 0;

    const handleFocusOnMax = () => {
        if (maxDosePosition) {
            focusOnPoint(maxDosePosition);
        }
    };

    const relativeDoseInPercent = useMemo(
        () =>
            dose
                ? relativeDosePercent(
                      dose.displayMaxDoseGy,
                      dose.normalizationValueGy
                  )
                : undefined,
        [dose]
    );

    const colorwashStep = useMemo(
        () => calculateColorwashStep(relativeDoseInPercent),
        [relativeDoseInPercent]
    );

    const doseLevelLabel = useMemo(
        () => generateDoseLevelLabel(dose, doseScale, relativeDoseInPercent),
        [dose, relativeDoseInPercent, doseScale]
    );

    // Fires on every drag frame: preview only, no domain commit.
    const onColorwashValueChange = (
        _e: Event,
        value: number[] | number
    ): void => {
        const [min, max] = value as number[];
        if (doseRange && max !== min) {
            previewDoseRange(vec2.fromValues(min, max));
        }
    };

    // Fires once when the gesture ends: this is the domain commit. The
    // slider also reports an end for a key that moves no thumb (a Page key,
    // a Shift+Arrow), which commits nothing. A thumb let go on the other
    // one ends on no valid window, so the last window previewed, which is
    // the one drawn, is the one committed.
    const onColorwashValueCommitted = (
        event: Event | React.SyntheticEvent,
        value: number[] | number
    ): void => {
        if (!doseRange || event.type === 'keydown') {
            return;
        }
        const [min, max] = value as number[];
        commitDoseRange(max !== min ? vec2.fromValues(min, max) : doseRange);
    };

    // Grabbing the slider thumb mid-edit must commit the typed value first.
    // The valueLabel's Box stops pointer propagation on the input, so this
    // handler only fires for thumb/rail grabs — never for clicks on the
    // editable percent itself. Blurring fires the input's existing onBlur
    // path (commit if parseable + valid + changed; revert otherwise).
    const sliderRef = useRef<HTMLSpanElement>(null);
    const onSliderPointerDown = (): void => {
        const active = document.activeElement;
        if (
            active instanceof HTMLInputElement &&
            sliderRef.current?.contains(active)
        ) {
            active.blur();
        }
    };

    /**
     * Convert a normalised slider value to its displayed % of prescription.
     * Mirrors the formula used in `valueLabelFormat` (and historically the
     * only readout the user had).
     */
    const sliderValueToPercent = useCallback(
        (sliderValue: number): number => {
            return relativeDoseInPercent
                ? sliderValue * relativeDoseInPercent
                : sliderValue * 100;
        },
        [relativeDoseInPercent]
    );

    /** A typed percent on the slider's scale, snapped to its step. */
    const snappedSliderValue = useCallback(
        (pct: number): number =>
            snapToStep(
                percentToSliderValue(pct, relativeDoseInPercent),
                colorwashStep
            ),
        [relativeDoseInPercent, colorwashStep]
    );

    // A typed percent the lower threshold takes: on the 0..1 window the
    // domain accepts, and below the upper threshold once snapped. A typed
    // percent is never clamped, so 500 % is refused rather than reaching the
    // domain and throwing out of a blur handler; the field then reverts.
    const acceptsMinPercent = useCallback(
        (pct: number): boolean => {
            if (!doseRange) {
                return false;
            }
            const snapped = snappedSliderValue(pct);
            return snapped >= 0 && snapped < doseRange[1];
        },
        [doseRange, snappedSliderValue]
    );

    // Mirror of acceptsMinPercent: above the lower threshold, at most 1.
    const acceptsMaxPercent = useCallback(
        (pct: number): boolean => {
            if (!doseRange) {
                return false;
            }
            const snapped = snappedSliderValue(pct);
            return snapped > doseRange[0] && snapped <= 1;
        },
        [doseRange, snappedSliderValue]
    );

    const handleMinCommit = useCallback(
        (newPct: number): void => {
            if (doseRange) {
                commitDoseRange(
                    vec2.fromValues(snappedSliderValue(newPct), doseRange[1])
                );
            }
        },
        [doseRange, snappedSliderValue, commitDoseRange]
    );

    const handleMaxCommit = useCallback(
        (newPct: number): void => {
            if (doseRange) {
                commitDoseRange(
                    vec2.fromValues(doseRange[0], snappedSliderValue(newPct))
                );
            }
        },
        [doseRange, snappedSliderValue, commitDoseRange]
    );

    const formatPercent = (pct: number): string => `${pct.toFixed(0)} %`;

    if (!dose) {
        return null;
    }

    // The title row (eye + Imported/Generated Dose label) stays visible
    // whenever a dose is loaded, so the user can re-show dose from the
    // viewport after hiding it. Max / Normalized / gradient only render
    // when dose is actually visible.
    const showColorwashDetails = doseVisibility && doseRange;

    return (
        <Box display="grid" gap={2}>
            <OverlayRow ml={-1} flexDirection="column">
                <Box
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    gap={0.5}
                >
                    <DoseVisibilityToggle />
                    <DoseOriginLabel />
                </Box>
                {showColorwashDetails && (
                    <>
                        {doseLevelLabel && maxDosePosition ? (
                            <NavigateToMaxButton
                                label={intl.formatMessage(
                                    { id: 'dose.focusOnMaxDose' },
                                    { value: maxDoseGyDisplay.toFixed(1) }
                                )}
                                onClick={handleFocusOnMax}
                                testId="dose-overlay-focus-max-button"
                            >
                                {doseLevelLabel}
                            </NavigateToMaxButton>
                        ) : (
                            <OverlayLabel>{doseLevelLabel}</OverlayLabel>
                        )}
                        {dose.displayMaxDoseGy.toFixed(1) !==
                            dose.normalizationValueGy.toFixed(1) && (
                            <OverlayLabel>
                                Normalized to 100%:{' '}
                                {dose.normalizationValueGy.toFixed(1)} Gy
                            </OverlayLabel>
                        )}
                    </>
                )}
            </OverlayRow>
            {showColorwashDetails && (
                <ColorwashSlider
                    ref={sliderRef}
                    orientation="vertical"
                    value={[doseRange[0] || 0, doseRange[1] || 0]}
                    step={colorwashStep}
                    // Page keys and Shift+Arrow are slice keys of the
                    // views, so they move no thumb.
                    shiftStep={0}
                    disableSwap
                    min={0}
                    max={1}
                    size="small"
                    onChange={onColorwashValueChange}
                    onChangeCommitted={onColorwashValueCommitted}
                    onPointerDown={onSliderPointerDown}
                    valueLabelDisplay="on"
                    data-testid="dose-colorwash-slider"
                    valueLabelFormat={(value, index) => {
                        const isMax = index === 1;
                        return (
                            <Box
                                p={0.5}
                                borderRadius={1}
                                display="flex"
                                flexDirection="column"
                                gap={theme.spacing(0.25)}
                                sx={theme.mixins.glassOverlay}
                                // Block pointer/keyboard events from
                                // bubbling into the slider thumb so clicks
                                // on the editable percent don't start a
                                // drag.
                                onPointerDown={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                                onKeyDown={(e) => e.stopPropagation()}
                            >
                                <EditableNumericValue
                                    value={Math.round(
                                        sliderValueToPercent(value)
                                    )}
                                    onCommit={
                                        isMax
                                            ? handleMaxCommit
                                            : handleMinCommit
                                    }
                                    // What the threshold takes, so that
                                    // `1,500` reads as 1.5 %: 1500 % is no
                                    // value either threshold takes.
                                    validate={
                                        isMax
                                            ? acceptsMaxPercent
                                            : acceptsMinPercent
                                    }
                                    formatDisplay={formatPercent}
                                    // ArrowUp / ArrowDown step the typed
                                    // percent by one while it is edited,
                                    // within the slider's scale.
                                    step={1}
                                    min={0}
                                    max={sliderValueToPercent(1)}
                                    ariaLabel={
                                        isMax
                                            ? 'Dose colorwash maximum, percent of prescription'
                                            : 'Dose colorwash minimum, percent of prescription'
                                    }
                                    testId={
                                        isMax
                                            ? 'dose-colorwash-max-input'
                                            : 'dose-colorwash-min-input'
                                    }
                                    unitSuffix=" %"
                                />
                                <Typography fontSize={10} lineHeight={1}>
                                    {relativeToAbsoluteDose(
                                        value,
                                        dose.displayMaxDoseGy
                                    )}{' '}
                                    Gy
                                </Typography>
                            </Box>
                        );
                    }}
                />
            )}
        </Box>
    );
};

export default DoseColorwashSlider;
